/**
 * The emergency SOS control.
 *
 * ChowDash has no safety screen: the surface was cut to four pages, and an SOS
 * behind a navigation step is not acceptable to ship that way. So the control
 * itself is the shared unit and it appears in the two places a rider looks when
 * something is wrong — the dash session panel and the support section of the
 * inbox — with the send flow identical in both.
 *
 * The confirmation is not a formality: it states exactly what dispatch receives.
 */
import { useState } from 'react'
import { MinimalAlert, MinimalButton } from '@ovasabi/ui-minimal'
import { ChowModal } from '../../components/chow'
import { ShieldIcon } from '../../components/icons'
import { useDeliverySnapshot, useRiderLocationSnapshot } from '../../generated/prototypeRuntime'
import { commands, isLiveRuntime } from '../../lib/runtime'
import { activeDelivery, number, text, useRiderId, type Snapshot } from './records'

interface EmergencySosProps {
  /** `sm` for the session panel's inline row, `md` for a support section. */
  size?: 'sm' | 'md'
}

export const EmergencySos = ({ size = 'md' }: EmergencySosProps) => {
  const riderId = useRiderId()
  const locations = useRiderLocationSnapshot() as Snapshot
  const deliveries = useDeliverySnapshot() as Snapshot
  const [confirming, setConfirming] = useState(false)
  const [sent, setSent] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  const location = locations.records[0] ?? {}
  const active = activeDelivery(deliveries.records)

  const sendSos = async () => {
    if (busy) return
    setBusy(true)
    setError('')
    try {
      const payload = {
        rider_id: riderId,
        delivery_id: active ? text(active, ['id', 'delivery_id']) : '',
        latitude: number(location, ['latitude']),
        longitude: number(location, ['longitude']),
      }
      if (isLiveRuntime()) await commands.safety.triggerSos(payload)
      else void commands.safety.triggerSos(payload).catch(() => undefined)
      setSent(true)
      setConfirming(false)
    } catch {
      // A failed SOS must leave the rider able to try again, so the modal stays
      // open rather than closing over an alert that was never sent.
      setSent(false)
      setError('Your emergency alert was not sent. Please try again.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <MinimalButton
        tone="danger"
        size={size}
        loading={busy}
        leading={<ShieldIcon size={size === 'sm' ? 'sm' : 'md'} />}
        onClick={() => setConfirming(true)}
      >
        {sent ? 'Emergency alert sent' : 'Send emergency alert'}
      </MinimalButton>
      <ChowModal
        open={confirming}
        title="Send an emergency alert?"
        onClose={() => {
          if (!busy) setConfirming(false)
        }}
      >
        <p>
          Dispatch receives your rider id, active delivery and latest location. Use this only when
          you, the customer or the order is in immediate danger.
        </p>
        {error && (
          <MinimalAlert tone="danger" title="Alert not sent">
            {error}
          </MinimalAlert>
        )}
        <MinimalButton variant="secondary" disabled={busy} onClick={() => setConfirming(false)}>
          Cancel
        </MinimalButton>
        <MinimalButton tone="danger" loading={busy} onClick={() => void sendSos()}>
          Send SOS now
        </MinimalButton>
      </ChowModal>
    </>
  )
}
